<template>
    <div>
        <div class="integrations-container animated fadeIn">
            <b-card class="integrations-card">
                <div class="integrations-header mb-4">
                    <div>
                        <h4>{{ $t('integrationslabel') }}</h4>
                        <p class="text-muted mb-0">{{ $t('manageYourIntegrations') }}</p>
                    </div>
                    <b-button variant="primary" size="lg" @click="goToMarketplace">
                        <b-icon icon="plus-circle"></b-icon> {{ $t('addMore') }}
                    </b-button>
                </div>

                <!-- Loading State -->
                <div v-if="loading" class="loading-state">
                    <div class="integrations-grid">
                        <div v-for="i in 3" :key="i" class="integration-card-skeleton">
                            <b-skeleton animation="throb" height="80px" class="mb-3"></b-skeleton>
                            <b-skeleton animation="throb" width="60%" height="20px" class="mb-2"></b-skeleton>
                            <b-skeleton animation="throb" width="40%" height="16px"></b-skeleton>
                        </div>
                    </div>
                </div>

                <!-- Empty State -->
                <div v-else-if="ownedIntegrations.length === 0" class="empty-state">
                    <div class="empty-icon">
                        <b-icon icon="puzzle" font-scale="4"></b-icon>
                    </div>
                    <h3 class="empty-title">{{ $t('noIntegrationsYet') }}</h3>
                    <p class="empty-subtitle">{{ $t('noIntegrationsDescription') }}</p>
                    <b-button variant="primary" size="lg" @click="goToMarketplace">
                        <b-icon icon="shop"></b-icon> {{ $t('exploreMarketplace') }}
                    </b-button>
                </div>

                <!-- Integrations Grid -->
                <div v-else class="integrations-grid">
                    <div 
                        v-for="integration in ownedIntegrations" 
                        :key="integration.id"
                        class="integration-card"
                        :class="{ 'active': isActive(integration) }"
                    >
                        <div class="integration-icon">
                            <IntegrationIcon :icon="integration.template.icon" />
                        </div>
                        
                        <div class="integration-content">
                            <div class="integration-header">
                                <h5 class="integration-name">{{ $translateLabel(integration.label) }}</h5>
                                <b-badge 
                                    :variant="isActive(integration) ? 'success' : integration.isOwnedNotIntegrated ? 'warning' : 'secondary'" 
                                    pill
                                    class="status-badge"
                                >
                                    <b-icon :icon="isActive(integration) ? 'check-circle-fill' : integration.isOwnedNotIntegrated ? 'cart-check-fill' : 'circle'"></b-icon>
                                    {{ isActive(integration) ? $t('active') : integration.isOwnedNotIntegrated ? "" : $t('inactive') }}
                                </b-badge>
                            </div>
                            
                            <p class="integration-description" v-if="integration.description">
                                {{ $translateLabel(integration.description) }}
                            </p>
                            
                            <div class="integration-actions">
                                <!-- Active integrations: show Settings + Deactivate -->
                                <b-button 
                                    variant="outline-primary" 
                                    size="sm" 
                                    @click="goToSettings(integration)"
                                    v-if="isActive(integration)"
                                >
                                    <b-icon icon="gear"></b-icon> {{ $t('settings.title') }}
                                </b-button>
                                <b-button 
                                    variant="outline-danger" 
                                    size="sm" 
                                    @click="deactivateIntegration(integration)"
                                    v-if="isActive(integration)"
                                >
                                    <b-icon icon="pause-circle"></b-icon> {{ $t('deactivate') }}
                                </b-button>
                                
                                <!-- Owned but not integrated: show Integrate button -->
                                <b-button 
                                    variant="success" 
                                    size="sm" 
                                    @click="integrateIntegration(integration)"
                                    v-else-if="integration.isOwnedNotIntegrated"
                                >
                                    <b-icon icon="plug"></b-icon> {{ $t('integrate') }}
                                </b-button>
                                
                                <!-- Integrated but inactive: show Activate button -->
                                <template v-else>
                                    <b-button 
                                        variant="primary" 
                                        size="sm" 
                                        @click="activateIntegration(integration)"
                                        class="mr-2"
                                    >
                                        <b-icon icon="play-circle"></b-icon> {{ $t('activate') }}
                                    </b-button>
                                    <b-button 
                                        variant="danger" 
                                        size="sm" 
                                        @click="confirmRemoveIntegration(integration)"
                                    >
                                        <b-icon icon="trash"></b-icon> {{ $t('marketplace.remove') }}
                                    </b-button>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </b-card>
        </div>
    
        <!-- Onboarding Required Modal -->
        <main-dialog
            v-model="showOnboardingModal"
            modal-class="onboarding-modal"
            centered
            hide-header
            hide-footer
            size="md"
        >
            <div class="onboarding-modal-content">
                <div class="onboarding-icon mb-4">
                    <b-icon icon="info-circle-fill" font-scale="3.5"></b-icon>
                </div>
                <h4 class="onboarding-title mb-3">{{ $t('marketplace.onboardingRequired') }}</h4>
                <p class="onboarding-message mb-4">{{ onboardingMessage }}</p>
                <div class="onboarding-actions">
                    <b-button 
                        variant="outline-light" 
                        @click="cancelOnboarding"
                        class="cancel-btn"
                    >
                        {{ $t('cancel') }}
                    </b-button>
                    <b-button 
                        variant="primary" 
                        @click="startOnboarding"
                        class="primary-btn"
                    >
                        {{ $t('marketplace.finishOnboarding') }}
                    </b-button>
                </div>
            </div>
        </main-dialog>
        
        <!-- Confirmation Modal -->
        <ConfirmModal
            :show="confirmModal.show"
            :title="confirmModal.title"
            :message="confirmModal.message"
            :confirmText="confirmModal.confirmText"
            :dangerButton="confirmModal.danger"
            :icon="confirmModal.icon"
            @confirm="handleConfirmAction"
            @cancel="closeConfirmModal"
        />
    </div>
</template>

<script>
import IntegrationIcon from '../../integrations/Icon'
import ConfirmModal from '@/components/workflow/ConfirmModal.vue'

export default {
    name: 'IntegrationsList',
    
    components: {
        IntegrationIcon,
        ConfirmModal
    },
    
    data() {
        return {
            loading: true,
            integrations: [],
            activeIntegrations: [],
            showOnboardingModal: false,
            onboardingMessage: '',
            onboardingIntegrationId: null,
            confirmModal: {
                show: false,
                title: '',
                message: '',
                confirmText: 'Confirm',
                danger: false,
                icon: 'warning',
                action: null,
                params: null
            }
        }
    },
    
    computed: {
        ownedIntegrations() {
            // Get subscriptions to find owned integrations
            const subscriptions = this.$store.getters.getSubscriptions || []
            console.log('Subscriptions:', subscriptions)
            console.log('First subscription full object:', subscriptions[0])
            
            // Get all integration IDs from subscriptions
            // Subscriptions might reference integrations by product_id or integration_id or reference
            const subscribedProductIds = new Set(
                subscriptions.map(sub => {
                    // Try different possible field names for the product/integration reference
                    const ref = sub.product_reference || sub.product_id || sub.reference || sub.product || sub.integration_id
                    console.log('Subscription product ref:', ref, 'from subscription:', sub)
                    return ref
                }).filter(Boolean) // Remove undefined/null values
            )
            console.log('Subscribed Product IDs:', Array.from(subscribedProductIds))
            
            // Get all integration IDs that are already integrated
            const integratedIds = new Set(
                this.activeIntegrations.map(integration => integration.id)
            )
            console.log('Integrated IDs:', Array.from(integratedIds))
            console.log('Available marketplace integrations:', this.integrations)
            
            const allIntegrations = []
            const seenIds = new Set()
            
            // First, add all already integrated integrations (from activeIntegrations)
            this.activeIntegrations.forEach(integration => {
                if (!seenIds.has(integration.id)) {
                    seenIds.add(integration.id)
                    allIntegrations.push(integration)
                }
            })
            
            // Then, add subscribed integrations that are NOT yet integrated
            // Match by either integration.id or integration.product_id
            this.integrations.forEach(integration => {
                const hasSubscription = subscribedProductIds.has(integration.id) || 
                                       subscribedProductIds.has(integration.product_id)
                const alreadyIntegrated = seenIds.has(integration.id)
                
                console.log(`Integration ${integration.id}:`, {
                    hasSubscription,
                    alreadyIntegrated,
                    willAdd: hasSubscription && !alreadyIntegrated,
                    productId: integration.product_id
                })
                
                if (!seenIds.has(integration.id) && hasSubscription) {
                    seenIds.add(integration.id)
                    // Mark this as owned but not integrated and normalize data structure
                    allIntegrations.push({
                        id: integration.id,
                        label: integration.label || integration.name,
                        description: integration.description,
                        template: {
                            icon: integration.icon || (integration.template && integration.template.icon)
                        },
                        isOwnedNotIntegrated: true
                    })
                }
            })
            
            console.log('Final ownedIntegrations:', allIntegrations)
            return allIntegrations
        }
    },
    
    methods: {
        isActive(integration) {
            // Check if integration has a status field, otherwise fall back to activeIntegrations check
            if (integration.status !== undefined) {
                return integration.status === 'active'
            }

            return this.activeIntegrations.some(active => active.id === integration.id)
        },
        
        isIntegrated(integration) {
            // Check if integration exists in activeIntegrations (regardless of status)
            return this.activeIntegrations.some(active => active.id === integration.id)
        },
        
        goToSettings(integration) {
            this.$router.push({
                path: `/admin/integrations/${integration.id}`
            })
        },
        
        goToMarketplace() {
            this.$router.push({
                path: '/admin/marketplace',
                query: { category: 'integrations' }
            })
        },
        
        activateIntegration(integration) {
            const popup = this.$loadingPopup(this.$t('activatingIntegration'))
            
            this.$integrationApi.post(`/integration/${integration.id}/activate`).then(() => {
                // Update integration status locally
                integration.status = 'active'
                
                this.$bvToast.toast(this.$t('integrationActivated'), {
                    variant: 'success',
                    title: this.$t('success')
                })
            }).catch((err) => {
                console.error('Integration activation error:', err)
                
                const errorData = err.response && err.response.data
                
                // Check if integration requires onboarding
                if (errorData && errorData.require_onboarding && errorData.integration) {
                    this.onboardingMessage = this.$t('marketplace.integrationRequiresOnboarding')
                    this.onboardingIntegrationId = integration.id
                    this.showOnboardingModal = true
                } else {
                    this.$bvToast.toast(
                        (errorData && errorData.message) || this.$t('failedToActivate'), 
                        {
                            variant: 'danger',
                            title: this.$t('error')
                        }
                    )
                }
            }).finally(() => {
                popup.hide()
            })
        },
        
        integrateIntegration(integration) {
            const popup = this.$loadingPopup(this.$t('integratingIntegration'))
            
            
            const payload = {
                id: integration.id,
                data: {},
                integrator: 'platform'
            }
            
            this.$integrationApi.post('/integrate', payload).then(() => {
                // Reload active integrations to get the newly integrated one
                return this.loadActiveIntegrations()
            }).then(() => {
                this.$bvToast.toast(this.$t('integrationIntegrated'), {
                    variant: 'success',
                    title: this.$t('success')
                })
            }).catch((err) => {
                console.error('Integration installation error:', err)
                
                const errorData = err.response && err.response.data
                
                // Check if integration requires onboarding
                if (errorData && errorData.require_onboarding && errorData.integration) {
                    this.onboardingMessage = this.$t('marketplace.integrationRequiresOnboarding')
                    this.onboardingIntegrationId = integration.id
                    this.showOnboardingModal = true
                } else {
                    this.$bvToast.toast(
                        (errorData && errorData.message) || this.$t('failedToIntegrate'), 
                        {
                            variant: 'danger',
                            title: this.$t('error')
                        }
                    )
                }
            }).finally(() => {
                popup.hide()
            })
        },

        startOnboarding() {
            this.showOnboardingModal = false
            // Unpause integration onboarding to trigger the onboarding flow
            this.$store.commit("UNPAUSE_ONBOARDING_ALL")
        },

        cancelOnboarding() {
            this.showOnboardingModal = false
            this.onboardingMessage = ''
            this.onboardingIntegrationId = null
        },
        
        async deactivateIntegration(integration) {
            const confirm = await this.$mainDialog.msgBoxConfirm(this.$t('integrations.deactivateAreYouSure'))
            if (!confirm) return

            const popup = this.$loadingPopup(this.$t('deactivatingIntegration'))
            
            this.$integrationApi.post(`/integration/${integration.id}/deactivate`).then(() => {
                // Update integration status locally
                integration.status = 'inactive'
                
                this.$bvToast.toast(this.$t('integrationDeactivated'), {
                    variant: 'success',
                    title: this.$t('success')
                })
            }).catch((err) => {
                this.$debugError(err)
                this.$bvToast.toast(this.$t('failedToDeactivate'), {
                    variant: 'danger',
                    title: this.$t('error')
                })
            }).finally(() => {
                popup.hide()
            })
        },
        
        confirmRemoveIntegration(integration) {
            // Check if integration has dependencies
            const dependencies = integration.dependencies || []
            
            // Check if the only dependency is not "platform"
            if (dependencies.length === 1 && dependencies[0] !== 'platform') {
                const dependency = dependencies[0]
                
                // Check if it's a template dependency
                if (dependency.startsWith('template:')) {
                    this.$mainDialog.msgBoxOk(
                        this.$t('marketplace.cannotRemoveTemplateIntegration'),
                        {
                            title: this.$t('marketplace.partOfTemplate'),
                            okVariant: 'primary',
                            centered: true
                        }
                    )
                    return
                }
                
                // Generic message for other dependencies
                this.$mainDialog.msgBoxOk(
                    this.$t('marketplace.cannotRemoveIntegrationWithDependencies'),
                    {
                        title: this.$t('marketplace.cannotRemove'),
                        okVariant: 'primary',
                        centered: true
                    }
                )
                return
            }
            
            this.confirmModal = {
                show: true,
                title: this.$t('marketplace.remove'),
                message: this.$t('marketplace.confirmRemoveIntegration'),
                confirmText: this.$t('marketplace.remove'),
                danger: true,
                icon: 'danger',
                action: 'removeIntegration',
                params: integration
            }
        },
        
        handleConfirmAction() {
            if (this.confirmModal.action === 'removeIntegration') {
                this.removeIntegration(this.confirmModal.params)
            }
            this.closeConfirmModal()
        },
        
        closeConfirmModal() {
            this.confirmModal = {
                show: false,
                title: '',
                message: '',
                confirmText: 'Confirm',
                danger: false,
                icon: 'warning',
                action: null,
                params: null
            }
        },
        
        removeIntegration(integration) {
            const popup = this.$loadingPopup(this.$t('marketplace.removingIntegration'))
            
            this.$integrationApi.put('/integrate', {
                id: integration.id
            }).then(() => {
                this.$store.dispatch('fetchIntegrations', true)
                
                this.$bvToast.toast(this.$t('marketplace.integrationRemoved'), {
                    variant: 'success',
                    title: this.$t('success'),
                    autoHideDelay: 3000
                })
                
                // Reload the integrations list
                this.loadActiveIntegrations()
            }).catch(err => {
                this.$debugError(err)
                this.$bvToast.toast(
                    (err.response && err.response.data && err.response.data.message) || this.$t('marketplace.failedToRemove'),
                    {
                        variant: 'danger',
                        title: this.$t('error')
                    }
                )
            }).finally(() => {
                popup.hide()
            })
        },
        
        async loadIntegrations() {
            try {
                const data = await this.$store.dispatch("fetchIntegrationsExplore")
                this.integrations = data || []
            } catch (err) {
                this.$debugError(err)
                this.$bvToast.toast(this.$t('failedToLoadIntegrations'), {
                    variant: 'danger',
                    title: this.$t('error')
                })
            }
        },
        
        async loadActiveIntegrations() {
            try {
                await this.$store.dispatch('fetchIntegrations')
                this.activeIntegrations = this.$store.getters.getIntegrations || []
            } catch (err) {
                this.$debugError(err)
            }
        },
        
        async loadData() {
            this.loading = true
            
            await Promise.all([
                this.$store.dispatch('fetchSubscriptions'),
                this.loadIntegrations(),
                this.loadActiveIntegrations()
            ])
            
            this.loading = false
        }
    },
    
    async mounted() {
        await this.loadData()
    }
}
</script>

<style scoped lang="scss">
@import '@/assets/scss/style.scss';
@import '@/assets/scss/_custom.scss';

.integrations-container {
    max-width: 1400px;
    margin: 0 auto;
}

.integrations-card {
    .integrations-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        
        h4 {
            color: var(--text-primary);
            font-weight: 600;
            margin: 0 0 4px 0;
        }
        
        p {
            font-size: 14px;
        }
    }
}

.loading-state,
.integrations-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 20px;
    margin-top: 20px;
}

.integration-card-skeleton {
    background: $dark-hard2-background;
    border: 1px solid $dark-border2;
    border-radius: 12px;
    padding: 20px;
}

.integration-card {
    background: $dark-hard2-background;
    border: 1px solid $dark-border2;
    border-radius: 12px;
    padding: 20px;
    display: flex;
    gap: 16px;
    transition: all 0.2s;
    
    &.active {
        border-color: rgba(102, 126, 234, 0.5);
        background: rgba(49, 51, 56, 0.8);
    }
    
    &:hover {
        border-color: $dark-border;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    }
    
    .integration-icon {
        flex-shrink: 0;
        width: 60px;
        height: 60px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: $dark-background;
        border-radius: 12px;
        padding: 12px;
    }
    
    .integration-content {
        flex: 1;
        min-width: 0;
        
        .integration-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 8px;
            gap: 12px;
            
            .integration-name {
                color: var(--text-primary);
                font-size: 16px;
                font-weight: 600;
                margin: 0;
                flex: 1;
            }
            
            .status-badge {
                font-size: 11px;
                font-weight: 500;
                padding: 4px 10px;
                display: inline-flex;
                align-items: center;
                gap: 4px;
                flex-shrink: 0;
            }
        }
        
        .integration-description {
            font-size: 13px;
            color: var(--text-secondary);
            margin: 0 0 12px 0;
            line-height: 1.5;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
        }
        
        .integration-actions {
            display: flex;
            gap: 8px;
            
            .btn {
                font-size: 13px;
                padding: 6px 14px;
                display: inline-flex;
                align-items: center;
                gap: 6px;
            }
        }
    }
}

.empty-state {
    text-align: center;
    padding: 80px 20px;
    
    .empty-icon {
        margin-bottom: 24px;
        
        svg {
            color: var(--text-muted);
        }
    }
    
    .empty-title {
        color: var(--text-primary);
        font-size: 24px;
        font-weight: 600;
        margin-bottom: 12px;
    }
    
    .empty-subtitle {
        font-size: 16px;
        color: var(--text-secondary);
        margin-bottom: 32px;
        max-width: 500px;
        margin-left: auto;
        margin-right: auto;
    }
}

// Onboarding Modal Styles
::v-deep .onboarding-modal {
    .modal-content {
        background: $dark-hard2-background;
        border: 1px solid $dark-border;
        border-radius: 12px;
    }

    .modal-body {
        padding: 2rem;
    }
}

.onboarding-modal-content {
    text-align: center;

    .onboarding-icon {
        color: $primary;
    }

    .onboarding-title {
        color: $light;
        font-weight: 600;
    }

    .onboarding-message {
        color: #ccc;
        font-size: 15px;
        line-height: 1.5;
    }

    .onboarding-actions {
        display: flex;
        gap: 12px;
        justify-content: center;

        .cancel-btn, .primary-btn {
            min-width: 120px;
            padding: 10px 20px;
            font-weight: 500;
        }

        .cancel-btn {
            border-color: $dark-border2;
            color: #ccc;

            &:hover {
                background: $dark-hard-background;
                border-color: $dark-border;
                color: $light;
            }
        }
    }
}
</style>
